import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import Loading from '../../../cors/Loading';
import { toast } from 'sonner';
import LivreurPaymentList from '../../../elements/livreur_payment_list';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { MdOutlineFileDownloadDone, MdAttachMoney } from "react-icons/md";

export default function AdminLivreurPayments() {
  const { token, role, roles } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async (page = 1) => {
    try {
      const res = await fetch(`${BASE_URL}/api/livreur-invoices?page=${page}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setInvoices(data.invoices.data);  
        setCurrentPage(data.invoices.current_page);
        setLastPage(data.invoices.last_page);
        setPerPage(data.invoices.per_page);
        setTotal(data.invoices.total);
        setStats(data.stats); 
        setError(null);
      } else {
        setError(data.message || "Failed to fetch invoices");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  const pagination = {
    currentPage,
    lastPage,
    total,
    from: (currentPage - 1) * perPage + 1,
    to: Math.min(currentPage * perPage, total)
  };

  const handleGeneratePayment = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/livreur-auto-invoices`, {
        method: 'GET',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json' 
        }
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(data.message);
        fetchInvoices();
      } else {
        toast.warning(data.message);
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      fetchInvoices(newPage);
    }
  };

  const prevPage = () => handlePageChange(currentPage - 1);
  const nextPage = () => handlePageChange(currentPage + 1);
  const paginate = (pageNum) => handlePageChange(pageNum);

  const { totalInvoices, pendingInvoices, paidInvoices, totalAmount } = stats;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              Livreur Payments
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              Manage and generate payments for all livreurs on your platform.
            </p>
          </div>
          {
            role === 1 || (role === 2 && roles.view_confirmator_invoices === 1) ? ( // Using similar permission for now
              <div className="mt-6 md:mt-0 flex gap-4">
                <button 
                  onClick={handleGeneratePayment}
                  className="cursor-pointer text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300"
                >
                  Générer les paiements
                </button>
              </div>
            ): ''
          }
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">Total Invoices</p>
                <p className="text-3xl font-bold text-teal-600">{totalInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">Pending Invoices</p>
                <p className="text-3xl font-bold text-yellow-600">{pendingInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">Paid Invoices</p>
                <p className="text-3xl font-bold text-green-600">{paidInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MdOutlineFileDownloadDone className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">Total Amount</p>
                <p className="text-2xl font-bold text-blue-600">{totalAmount} MAD</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MdAttachMoney className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-8">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by livreur name, email, or invoice number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loading />
          </div>
        )}

        {error && <p className="text-center text-red-500 py-10">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8 flex justify-between items-center">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-xs font-medium">
                {invoices.length} {invoices.length === 1 ? "invoice found" : "invoices found"}
              </span>
            </div>

            {/* Invoices List */}
            {invoices.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-x-auto custom-scrollbar">
                  <div className="min-w-[60rem]">
                    <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="pl-6 pr-2 py-4 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider">Invoice</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Livreur</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Orders</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Amount</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Payment Date</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="pr-6 pl-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((invoice, key) => (
                        <LivreurPaymentList key={key} role={role} roles={roles} invoice={invoice} fetchInvoices={fetchInvoices} />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
            ) : (
              /* Empty State */
              <div className="text-center py-0">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">No invoices found</h3>
                {
                  role === 1 &&
                  <div>
                    <p className="text-gray-600 text-sm mb-6">Try adjusting your search terms or generate new invoices.</p>
                    <button
                      onClick={handleGeneratePayment}
                      className="cursor-pointer text-xs bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold py-2 px-6 rounded-xl transition-all duration-300"
                    >
                      Generate Invoices
                    </button>
                  </div>
                }
              </div>
            )}

            {/* Enhanced Pagination */}
            {lastPage > 1 && (
              <div className="bg-white px-4 py-3 sm:px-6 mt-6 rounded-b-2xl">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Affichage <span className="font-medium">{pagination.from}</span> à{" "}
                    <span className="font-medium">{pagination.to}</span> sur{" "}
                    <span className="font-medium">{pagination.total}</span> résultats
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={pagination.currentPage === 1}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-3 py-1 cursor-pointer text-sm rounded-md ${pagination.currentPage === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <button
                          onClick={() => paginate(pagination.lastPage)}
                          className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                        >
                          {pagination.lastPage}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={nextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === pagination.lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
